import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquareText, RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, dateTime } from '../lib/api';
import WhatsAppComposerModal from './WhatsAppComposerModal';

const req=(method,body)=>({method,headers:{'content-type':'application/json'},body:JSON.stringify(body)});
const normalisePhone=value=>{
  let phone=String(value||'').trim().replace(/[^\d+]/g,'');
  if(phone.startsWith('00'))phone='+'+phone.slice(2);
  if(/^0\d{9,10}$/.test(phone))phone='+44'+phone.slice(1);
  if(/^44\d{9,12}$/.test(phone))phone='+'+phone;
  return /^\+[1-9]\d{7,14}$/.test(phone)?phone:'';
};
const isPhoneType=value=>['whatsapp','mobile','mobile_phone','phone','telephone'].includes(String(value||'').toLowerCase());
const title=value=>String(value??'').replaceAll('_',' ').replace(/\b\w/g,char=>char.toUpperCase());

export default function CustomerWhatsAppControls({customerId,customerName='',contacts=[]}){
  const navigate=useNavigate();
  const[data,setData]=useState({preference:null,thread:null,messages:[]});
  const[phone,setPhone]=useState('');
  const[serviceOptIn,setServiceOptIn]=useState(false);
  const[marketingOptIn,setMarketingOptIn]=useState(false);
  const[composerOpen,setComposerOpen]=useState(false);
  const[loading,setLoading]=useState(true);
  const[busy,setBusy]=useState(false);
  const[notice,setNotice]=useState('');

  const candidatePhones=useMemo(()=>contacts
    .filter(row=>isPhoneType(row.contact_type))
    .map(row=>({id:row.id,value:normalisePhone(row.contact_value),type:row.contact_type,primary:Boolean(row.is_primary),verified:String(row.verification_status||'').toLowerCase()==='verified'||Boolean(row.verified_at)}))
    .filter(row=>row.value)
    .sort((a,b)=>Number(b.type==='whatsapp')-Number(a.type==='whatsapp')||Number(b.primary)-Number(a.primary)||Number(b.verified)-Number(a.verified)),[contacts]);

  const load=useCallback(async()=>{
    setLoading(true);setNotice('');
    try{
      const [prefResult,inboxResult]=await Promise.all([
        api('/api/whatsapp/preferences/'+encodeURIComponent(customerId)).catch(()=>({preference:null})),
        api('/api/whatsapp/inbox').catch(()=>({threads:[]})),
      ]);
      const preference=prefResult.preference||null;
      const thread=(inboxResult.threads||[]).find(row=>String(row.customer_id||'')===String(customerId))||
        (preference?.phone_e164?(inboxResult.threads||[]).find(row=>row.phone_e164===preference.phone_e164):null)||
        candidatePhones.map(row=>(inboxResult.threads||[]).find(thread=>thread.phone_e164===row.value)).find(Boolean)||null;
      const resolvedPhone=preference?.phone_e164||thread?.phone_e164||candidatePhones[0]?.value||'';
      let messages=[];
      if(resolvedPhone){
        const history=await api('/api/whatsapp/conversations/history?phone='+encodeURIComponent(resolvedPhone)+'&limit=12').catch(()=>({messages:[]}));
        messages=history.messages||[];
      }
      setData({preference,thread,messages});
      setPhone(resolvedPhone);
      setServiceOptIn(Number(preference?.service_opt_in||0)===1);
      setMarketingOptIn(Number(preference?.marketing_opt_in||0)===1);
    }catch(error){
      setNotice('Error: '+(error.message||'WhatsApp customer controls could not load.'));
    }finally{setLoading(false);}
  },[customerId,candidatePhones]);

  useEffect(()=>{load();},[load]);

  useEffect(()=>{
    const open=()=>setComposerOpen(true);
    window.addEventListener('customer360-open-whatsapp-composer',open);
    return()=>window.removeEventListener('customer360-open-whatsapp-composer',open);
  },[]);

  const serviceWindowOpen=Boolean(data.thread?.last_inbound_at&&Date.now()-Date.parse(data.thread.last_inbound_at)<24*60*60*1000);

  const savePreferences=async()=>{
    if(!phone){setNotice('Error: Select a WhatsApp/mobile number first.');return;}
    setBusy(true);setNotice('');
    try{
      await api('/api/whatsapp/preferences/'+encodeURIComponent(customerId),req('PUT',{
        phone,
        serviceOptIn,
        marketingOptIn,
        source:'customer360',
      }));
      setNotice('WhatsApp contact preferences saved.');
      await load();
    }catch(error){setNotice('Error: '+(error.message||'WhatsApp preferences could not be saved.'));}
    finally{setBusy(false);}
  };

  const openInbox=()=>{
    if(!phone)return;
    navigate('/whatsapp-inbox?phone='+encodeURIComponent(phone)+'&customer='+encodeURIComponent(customerId));
  };

  const onSent=async(_result,sentPhone)=>{
    setComposerOpen(false);
    if(sentPhone)setPhone(sentPhone);
    setNotice('WhatsApp message sent from Customer360.');
    await load();
  };

  return <section className="c360-whatsapp-panel">
    <header className="c360-whatsapp-head">
      <div><span>WHATSAPP</span><h3 className="govuk-heading-s">WhatsApp conversation</h3><p className="govuk-body">Message this customer, review recent WhatsApp history and manage the CRM contact preference without leaving Customer360.</p></div>
      <div className="c360-whatsapp-actions">
        <button className="button secondary small govuk-button govuk-button--secondary" type="button" onClick={load} disabled={loading||busy}><RefreshCw size={14}/>Refresh</button>
        <button className="button secondary small govuk-button govuk-button--secondary" type="button" onClick={openInbox} disabled={!phone}><MessageSquareText size={14}/>Open inbox</button>
        <button className="button primary small govuk-button" type="button" onClick={()=>setComposerOpen(true)} disabled={!phone}><Send size={14}/>Send WhatsApp</button>
      </div>
    </header>

    {notice&&<div className={'operation-notice '+(notice.startsWith('Error:')?'error':'success')}>{notice}</div>}

    <div className="c360-whatsapp-status">
      <div><span>Number</span><strong>{phone||'No mobile / WhatsApp number'}</strong></div>
      <div><span>Service window</span><strong>{serviceWindowOpen?'Open':'Closed'}</strong></div>
      <div><span>Unread</span><strong>{Number(data.thread?.unread_count||0)}</strong></div>
      <div><span>Conversation</span><strong>{data.thread?title(data.thread.status||'open'):'Not started'}</strong></div>
    </div>

    <div className="c360-whatsapp-preferences">
      <label className="govuk-label"><span>WhatsApp number</span><select className="govuk-select" value={phone} onChange={event=>{const next=event.target.value;setPhone(next);const same=next&&next===data.preference?.phone_e164;setServiceOptIn(Boolean(same&&Number(data.preference?.service_opt_in||0)===1));setMarketingOptIn(Boolean(same&&Number(data.preference?.marketing_opt_in||0)===1));}} disabled={busy}><option value="">Select number</option>{candidatePhones.map(row=><option key={row.id||row.value} value={row.value}>{row.value} · {title(row.type)}{row.primary?' · Primary':''}{row.verified?' · Verified':''}</option>)}{phone&&!candidatePhones.some(row=>row.value===phone)&&<option value={phone}>{phone} · Saved WhatsApp preference</option>}</select></label>
      <label className="c360-whatsapp-check govuk-label"><input type="checkbox" checked={serviceOptIn} onChange={event=>setServiceOptIn(event.target.checked)} disabled={busy}/><span><strong>Service messages</strong><small>Record only where the customer has chosen or otherwise validly agreed to this contact route.</small></span></label>
      <label className="c360-whatsapp-check govuk-label"><input type="checkbox" checked={marketingOptIn} onChange={event=>setMarketingOptIn(event.target.checked)} disabled={busy}/><span><strong>Marketing messages</strong><small>Keep separate from service-message consent.</small></span></label>
      <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={savePreferences} disabled={busy||!phone}><ShieldCheck size={15}/>{busy?'Saving…':'Save WhatsApp preferences'}</button>
    </div>

    <div className="c360-whatsapp-history">
      <div className="c360-whatsapp-history-head"><strong>Recent WhatsApp history</strong><span>{data.messages.length} shown</span></div>
      {data.messages.length?<div className="c360-whatsapp-message-list">{data.messages.map(message=><article key={message.id||message.whatsapp_message_id} className={message.direction==='outbound'?'outbound':'inbound'}>
        <div><strong>{message.direction==='outbound'?'Sousa Murray':'Customer'}</strong><span>{message.body_preview||title(message.message_type||'message')}</span></div>
        <footer><span>{dateTime(message.received_at||message.sent_at||message.created_at)}</span><span>{title(message.status||message.direction)}</span></footer>
      </article>)}</div>:<div className="c360-whatsapp-empty">{loading?'Loading WhatsApp history…':'No WhatsApp messages are recorded for this customer yet.'}</div>}
    </div>

    {composerOpen&&<WhatsAppComposerModal customerId={customerId} customerName={customerName} initialPhone={phone} close={()=>!busy&&setComposerOpen(false)} onSent={onSent} source="customer360"/>}
  </section>;
}
